import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, Col, Row, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import WidgetSectionTitle from "widgets/WidgetSectionTitle";
import useInhouseEmails from "hooks/Inhouse emails/useInhouseEmails";
import ComposeEmailModal from "./ComposeEmailModal";
import IncomingEmailsTable from "./IncomingEmailsTable";
import OutgoingEmailsTable from "./OutgoingEmailsTable";

const normalizePage = (payload) => {
  if (Array.isArray(payload)) {
    return {
      content: payload,
      totalPages: 1,
      totalElements: payload.length,
    };
  }

  const data = payload?.data ?? payload;
  const content =
    (Array.isArray(data?.content) && data.content) ||
    (Array.isArray(data?.items) && data.items) ||
    (Array.isArray(data?.results) && data.results) ||
    [];

  return {
    content,
    totalPages: data?.totalPages ?? 1,
    totalElements: data?.totalElements ?? content.length,
  };
};

const createCorrelationId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  // Simple UUID v4 fallback for environments without crypto.randomUUID
  const template = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx";
  return template.replace(/[xy]/g, (char) => {
    const rand = (Math.random() * 16) | 0;
    const value = char === "x" ? rand : (rand & 0x3) | 0x8;
    return value.toString(16);
  });
};

export default function InhouseEmails({ defaultTab = "incoming" }) {
  const {
    getIncomingEmailsByAccountId,
    getOutgoingEmailsByAccountId,
    countUnreadEmails,
    sendEmail,
  } = useInhouseEmails();

  const [activeTab, setActiveTab] = useState(defaultTab);
  const [incomingEmails, setIncomingEmails] = useState([]);
  const [outgoingEmails, setOutgoingEmails] = useState([]);
  const [incomingPage, setIncomingPage] = useState(0);
  const [incomingPageSize, setIncomingPageSize] = useState(20);
  const [outgoingPage, setOutgoingPage] = useState(0);
  const [outgoingPageSize, setOutgoingPageSize] = useState(20);
  const [incomingTotals, setIncomingTotals] = useState({
    totalPages: 1,
    totalElements: 0,
  });
  const [outgoingTotals, setOutgoingTotals] = useState({
    totalPages: 1,
    totalElements: 0,
  });
  const [unreadCount, setUnreadCount] = useState(0);
  const [loadingIncoming, setLoadingIncoming] = useState(false);
  const [loadingOutgoing, setLoadingOutgoing] = useState(false);
  const [loadingUnread, setLoadingUnread] = useState(false);
  const [showCompose, setShowCompose] = useState(false);

  const loadIncoming = useCallback(async () => {
    setLoadingIncoming(true);
    try {
      const response = await getIncomingEmailsByAccountId({
        page: incomingPage,
        size: incomingPageSize,
      });
      const page = normalizePage(response);
      setIncomingEmails(page.content);
      setIncomingTotals({
        totalPages: page.totalPages,
        totalElements: page.totalElements,
      });
    } catch (err) {
      toast.error(err?.message || "Failed to fetch incoming emails", {
        theme: "colored",
      });
      setIncomingEmails([]);
      setIncomingTotals({ totalPages: 1, totalElements: 0 });
    } finally {
      setLoadingIncoming(false);
    }
  }, [getIncomingEmailsByAccountId, incomingPage, incomingPageSize]);

  const loadOutgoing = useCallback(async () => {
    setLoadingOutgoing(true);
    try {
      const response = await getOutgoingEmailsByAccountId({
        page: outgoingPage,
        size: outgoingPageSize,
      });
      const page = normalizePage(response);
      setOutgoingEmails(page.content);
      setOutgoingTotals({
        totalPages: page.totalPages,
        totalElements: page.totalElements,
      });
    } catch (err) {
      toast.error(err?.message || "Failed to fetch outgoing emails", {
        theme: "colored",
      });
      setOutgoingEmails([]);
      setOutgoingTotals({ totalPages: 1, totalElements: 0 });
    } finally {
      setLoadingOutgoing(false);
    }
  }, [getOutgoingEmailsByAccountId, outgoingPage, outgoingPageSize]);

  const loadUnreadCount = useCallback(async () => {
    setLoadingUnread(true);
    try {
      const response = await countUnreadEmails();
      const count = Number(response);
      setUnreadCount(Number.isFinite(count) ? count : 0);
    } catch (err) {
      toast.error(err?.message || "Failed to fetch unread count", {
        theme: "colored",
      });
      setUnreadCount(0);
    } finally {
      setLoadingUnread(false);
    }
  }, [countUnreadEmails]);

  useEffect(() => {
    loadIncoming();
  }, [loadIncoming]);

  useEffect(() => {
    loadOutgoing();
  }, [loadOutgoing]);

  useEffect(() => {
    loadUnreadCount();
  }, [loadUnreadCount]);

  useEffect(() => {
    setActiveTab(defaultTab);
  }, [defaultTab]);

  const handleSendEmail = useCallback(
    async (payload) => {
      try {
        await sendEmail(payload, { correlationId: createCorrelationId() });
        toast.success("Email sent successfully", { theme: "colored" });
        setShowCompose(false);
        await loadOutgoing();
      } catch (err) {
        toast.error(err?.message || "Failed to send email", {
          theme: "colored",
        });
      }
    },
    [loadOutgoing, sendEmail],
  );

  const unreadBadge = useMemo(() => {
    if (loadingUnread) {
      return <Spinner animation="border" size="sm" />;
    }
    return <Badge bg="danger">{unreadCount}</Badge>;
  }, [loadingUnread, unreadCount]);

  return (
    <>
      <WidgetSectionTitle
        icon="envelope"
        title="Inhouse Emails"
        subtitle="Track incoming and outgoing messages and send new emails."
        transform="shrink-2"
        className="mb-3 mt-3"
      />

      <Row className="align-items-center mb-3 g-2">
        <Col xs={12} md="auto">
          <div className="d-flex align-items-center gap-2">
            <Button
              variant={activeTab === "incoming" ? "success" : "outline-success"}
              size="sm"
              onClick={() => setActiveTab("incoming")}
            >
              Incoming
            </Button>
            <Button
              variant={activeTab === "outgoing" ? "success" : "outline-success"}
              size="sm"
              onClick={() => setActiveTab("outgoing")}
            >
              Outgoing
            </Button>
          </div>
        </Col>
        <Col xs={12} md="auto">
          <div className="d-flex align-items-center gap-2">
            <span className="text-muted small">Unread:</span>
            {unreadBadge}
          </div>
        </Col>
        <Col xs={12} md="auto">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={loadUnreadCount}
          >
            Refresh Count
          </Button>
        </Col>
        <Col className="text-md-end">
          <Button variant="success" size="sm" onClick={() => setShowCompose(true)}>
            Compose Email
          </Button>
        </Col>
      </Row>

      {activeTab === "incoming" ? (
        <IncomingEmailsTable
          emails={incomingEmails}
          loading={loadingIncoming}
          page={incomingPage}
          pageSize={incomingPageSize}
          totalPages={incomingTotals.totalPages}
          totalElements={incomingTotals.totalElements}
          setPage={setIncomingPage}
          setPageSize={setIncomingPageSize}
        />
      ) : (
        <OutgoingEmailsTable
          emails={outgoingEmails}
          loading={loadingOutgoing}
          page={outgoingPage}
          pageSize={outgoingPageSize}
          totalPages={outgoingTotals.totalPages}
          totalElements={outgoingTotals.totalElements}
          setPage={setOutgoingPage}
          setPageSize={setOutgoingPageSize}
        />
      )}

      <ComposeEmailModal
        show={showCompose}
        onClose={() => setShowCompose(false)}
        onSend={handleSendEmail}
      />
    </>
  );
}
